/** Page placeholders; callers retain the destination heading and navigation. */
import { BlockStack, Box, Card, Divider, InlineGrid, InlineStack, Text } from '@shopify/polaris'
import { SkeletonBodyText } from './shims/SkeletonBodyTextShim'
import './ShopifyPageSkeleton.css'

export type ShopifyPageSkeletonLayout = 'content' | 'index'

/** Pulsing placeholder lines for inline loading inside existing card frames. */
export function ShopifySkeletonText({ lines }: { lines: number }) {
  return (
    <div className="tf-loading-placeholder">
      <SkeletonBodyText lines={lines} />
    </div>
  )
}

function ContentSkeleton() {
  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Box maxWidth="12rem">
            <ShopifySkeletonText lines={1} />
          </Box>
          <ShopifySkeletonText lines={2} />
        </BlockStack>
      </Card>
      <Card>
        <ShopifySkeletonText lines={4} />
      </Card>
    </BlockStack>
  )
}

function IndexSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between">
          <Box width="10rem">
            <ShopifySkeletonText lines={1} />
          </Box>
          <Box width="6rem">
            <ShopifySkeletonText lines={1} />
          </Box>
        </InlineStack>
        <Divider />
        {Array.from({ length: 4 }, (_, index) => (
          <InlineGrid key={index} columns="2fr 1fr 1fr" gap="300" alignItems="center">
            <ShopifySkeletonText lines={2} />
            <ShopifySkeletonText lines={1} />
            <ShopifySkeletonText lines={1} />
          </InlineGrid>
        ))}
      </BlockStack>
    </Card>
  )
}

/** Reserves content with a gentle loading cue and no focusable placeholder controls. */
export function ShopifyPageSkeleton({
  layout = 'content',
  accessibilityLabel,
}: {
  layout?: ShopifyPageSkeletonLayout
  accessibilityLabel: string
}) {
  let placeholders = <ContentSkeleton />
  if (layout === 'index') placeholders = <IndexSkeleton />
  return (
    <div role="status" aria-label={accessibilityLabel} data-testid="shopify-page-skeleton">
      <Text as="span" visuallyHidden>
        {accessibilityLabel}
      </Text>
      <div aria-hidden="true">{placeholders}</div>
    </div>
  )
}
